import { Field, HStack, Input, Text } from '@chakra-ui/react'

import { Segmented } from '@src/components/sco/Advisors/shared/Segmented'
import {
  isSessionDurationMinutes,
  SESSION_MAX_MINUTES,
  SESSION_MIN_MINUTES,
  SESSION_PACKAGE_MINUTES,
} from '@src/components/sco/Advisors/utils/sessionFormat'
import { useEffect, useState } from 'react'

const PRESET_LABELS = SESSION_PACKAGE_MINUTES.map(minutes => `${minutes} min`)
const CUSTOM_LABEL = 'Custom'
const OPTIONS = [...PRESET_LABELS, CUSTOM_LABEL]

const isPresetMinutes = (minutes: number) =>
  SESSION_PACKAGE_MINUTES.includes(minutes as (typeof SESSION_PACKAGE_MINUTES)[number])

export const SessionDurationPicker = ({
  value,
  onChange,
}: {
  value: number
  onChange: (minutes: number) => void
}) => {
  const [customSelected, setCustomSelected] = useState(!isPresetMinutes(value))
  const [draft, setDraft] = useState(Number.isFinite(value) ? String(value) : '')

  useEffect(() => {
    if (!customSelected && Number.isFinite(value)) {
      setDraft(String(value))
    }
  }, [customSelected, value])

  const segmentValue = customSelected ? CUSTOM_LABEL : `${value} min`
  const parsedDraft = draft.trim() === '' ? NaN : Number(draft)
  const draftInvalid = customSelected && draft.trim() !== '' && !isSessionDurationMinutes(parsedDraft)
  const segmentedValue =
    !customSelected && PRESET_LABELS.includes(segmentValue as (typeof PRESET_LABELS)[number])
      ? segmentValue
      : CUSTOM_LABEL

  return (
    <Field.Root invalid={draftInvalid}>
      <Field.Label>Duration</Field.Label>
      <Segmented
        options={[...OPTIONS]}
        size={'md'}
        value={segmentedValue}
        onChange={next => {
          if (next === CUSTOM_LABEL) {
            setCustomSelected(true)
            setDraft(Number.isFinite(value) ? String(value) : '')
            return
          }
          const minutes = parseInt(next, 10)
          if (isPresetMinutes(minutes)) {
            setCustomSelected(false)
            setDraft(String(minutes))
            onChange(minutes)
          }
        }}
      />
      {customSelected && (
        <HStack gap={2}>
          <Input
            aria-label={'Custom duration in minutes'}
            max={SESSION_MAX_MINUTES}
            maxW={'7rem'}
            min={SESSION_MIN_MINUTES}
            step={1}
            type={'number'}
            value={draft}
            onChange={event => {
              const nextDraft = event.target.value
              setDraft(nextDraft)
              if (nextDraft.trim() === '') {
                onChange(Number.NaN)
                return
              }
              const minutes = Number(nextDraft)
              if (!isSessionDurationMinutes(minutes)) {
                onChange(Number.NaN)
                return
              }
              onChange(minutes)
            }}
          />
          <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
            minutes
          </Text>
        </HStack>
      )}
      {draftInvalid && (
        <Field.ErrorText>
          Enter a whole number between {SESSION_MIN_MINUTES} and {SESSION_MAX_MINUTES}.
        </Field.ErrorText>
      )}
    </Field.Root>
  )
}
